import React, { useEffect, useState } from "react";
import { X, Feather } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const LAST_KEY = "umbra:lastVisit";
const DISMISS_KEY = "umbra:nudgeDismissed";
const HOURS = 20;

/**
 * A gentle ritual nudge that appears if the user has been away for a while.
 * Stored in localStorage; auto-dismissed after 20 hours between visits.
 * Only rendered for authenticated users — signed-out visitors on the
 * landing page never see it (the "Return to the workbook" CTA is confusing
 * to a first-time viewer, and the fixed-position card was covering the
 * site footer's Contact link).
 */
export default function DailyNudge() {
  const { user } = useAuth();
  const location = useLocation();
  const [show, show_] = useState(false);
  const [message, setMessage] = useState("");

  // Suppress on purchase-adjacent + admin surfaces so CTAs stay unobstructed.
  const path = location.pathname;
  const suppressed =
    path.startsWith("/admin") ||
    path.startsWith("/pricing") ||
    path.startsWith("/partner") ||
    path.startsWith("/library");

  useEffect(() => {
    if (!user || suppressed) return;
    const now = Date.now();
    const last = Number(localStorage.getItem(LAST_KEY)) || 0;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY)) || 0;
    const hoursSince = (now - last) / (1000 * 60 * 60);
    const hoursSinceDismiss = (now - dismissed) / (1000 * 60 * 60);

    // Show if: never visited before OR been away 20+ hrs, AND not just dismissed within 4h
    const eligible = (last === 0 || hoursSince >= HOURS) && hoursSinceDismiss > 4;

    if (eligible) {
      const messages = [
        "The workbook is holding space for you. One prompt is enough for today.",
        "You don't have to finish a chapter. Just speak one honest sentence.",
        "The shadow does not need to be fixed. Only witnessed. Return gently.",
        "A small return counts. Even one breath, one prompt, one truth.",
        "You are not behind. The work waits at your pace.",
      ];
      setMessage(messages[Math.floor(Math.random() * messages.length)]);
      // Small delay so it feels intentional, not aggressive
      const t = setTimeout(() => show_(true), 1200);
      return () => clearTimeout(t);
    }

    // Update last visit
    localStorage.setItem(LAST_KEY, String(now));
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    localStorage.setItem(LAST_KEY, String(Date.now()));
    show_(false);
  };

  if (!show || suppressed) return null;

  return (
    <div
      data-testid="daily-nudge"
      className="fixed bottom-safe left-4 right-4 sm:left-6 sm:right-6 md:left-auto md:right-6 md:max-w-md z-40 animate-fade-up"
    >
      <div className="bg-obsidian-800/95 backdrop-blur-xl border border-obsidian-600 p-5 sm:p-6 relative">
        <button
          onClick={dismiss}
          data-testid="daily-nudge-close"
          className="absolute top-2 right-2 text-bone-500 hover:text-bone-200 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-4">
          <div className="shrink-0 h-9 w-9 border border-terracotta/50 flex items-center justify-center">
            <Feather className="h-4 w-4 text-terracotta" />
          </div>
          <div className="flex-1 pr-4">
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
              A quiet reminder
            </p>
            <p className="font-serif italic text-base text-bone-200 leading-relaxed mb-4">
              {message}
            </p>
            <Link
              to="/dashboard"
              onClick={dismiss}
              data-testid="daily-nudge-cta"
              className="inline-flex items-center min-h-[44px] px-1 py-2 sm:min-h-0 sm:py-1 font-sans text-xs uppercase tracking-[0.24em] text-terracotta hover:text-terracotta-soft border-b border-terracotta transition-colors"
            >
              Return to the workbook
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
